/**
 * `perdiem` — per-diems, buy-outs and cash flows on the road. ENTIRELY
 * financial scope (owner + editor only — never crew-visible on share views,
 * never promoters). Buy-outs are a category here, not schedule-title hacks
 * (Laurie's v1 feedback: "Buyouts for hotel and hotel credit must be added
 * to perdiem section").
 */
import { z } from "zod";
import { defineCollection, field } from "../define.ts";

const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");

export const perdiem = defineCollection({
  key: "perdiem",
  labelKey: "collections.perdiem",
  icon: "banknote",
  sheetTab: "Per diem",
  dedupFields: ["day", "category", "person_id"],
  sort: [{ field: "day" }],
  split: "gig",
  chatHint:
    "Money on the road: daily per-diems, meal buy-outs ('Diner 50€/pers'), hotel buy-outs, hotel " +
    "credits, cash advances. Financial scope — never visible to crew share links or promoters.",
  fields: {
    category: field({
      type: z.string().min(1),
      tier: "hot",
      pii: "financial",
      labelKey: "perdiem.category",
      suggestedChips: ["per diem", "meal buyout", "hotel buyout", "hotel credit", "advance"],
      sheet: { column: "Category" },
      description: "per diem | meal buyout | hotel buyout | hotel credit | advance | other.",
    }),
    person_id: field({
      type: z.string().optional(),
      tier: "hot",
      pii: "financial",
      labelKey: "perdiem.person",
      sheet: { column: "Person" },
      description: "Who receives it. Empty = per person for the whole party (note the multiplier in notes).",
    }),
    amount: field({
      type: z.number().optional(),
      tier: "hot",
      pii: "financial",
      labelKey: "perdiem.amount",
      sheet: { column: "Amount" },
      description: "Amount per person.",
    }),
    currency: field({
      type: z.string().max(3).optional(),
      tier: "hot",
      pii: "financial",
      labelKey: "perdiem.currency",
      suggestedChips: ["EUR", "USD", "GBP", "AUD", "CHF"],
      sheet: { column: "Currency" },
      description: "ISO currency code.",
    }),
    day: field({
      type: DAY.optional(),
      tier: "detail",
      pii: "financial",
      labelKey: "perdiem.day",
      renamedFrom: ["date"], // v1 wire name (migration lineage)
      sheet: { column: "Day" },
      description: "Which day it covers.",
    }),
    method: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "financial",
      labelKey: "perdiem.method",
      suggestedChips: ["cash", "transfer", "promoter pays"],
      sheet: { column: "Method" },
      description: "cash | transfer | promoter pays | …",
    }),
    status: field({
      type: z.enum(["due", "paid", "received"]).optional(),
      tier: "detail",
      pii: "financial",
      labelKey: "perdiem.status",
      sheet: { column: "Status" },
      description: "due | paid | received.",
    }),
    notes: field({
      type: z.string().optional(),
      tier: "detail",
      pii: "financial",
      labelKey: "perdiem.notes",
      sheet: { column: "Notes" },
      description: "Context (e.g. 'Diner 50€/pers, promoter pays cash at venue').",
    }),
  },
});
